import { put, call, select, getContext } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { merge as _merge } from 'lodash';
import { buildUrl, createErrorRemoveReducer, createErrorClearReducer, decorateEntity } from './utils';
import { template } from '../utils/misc';

export function createFetchDataSaga(url, entityDescriptor, defaultOptions, getFromList = false) {
  const { logger } = entityDescriptor;
  const urlTemplate = template(url);
  const responseProjection = defaultOptions.responseProjection || (input => input);
  const defaultFilters = defaultOptions.defaultFilters || {};

  const getLocale = createSelector(
    state => state.intl,
    state => state.locale
  );

  return function* fetchDataSaga({ payload: { options = {} } }) {
    try {
      let listItem;

      if (getFromList) {
        listItem = yield select(entityDescriptor.selectors.findOneFromList(options.id));
      }

      if (getFromList && listItem) {
        logger.debug('Take the entity from the list store. Options:', options);
        yield put(entityDescriptor.fetchDataComplete(listItem));
      } else {
        const http = yield getContext('http');
        const locale = yield select(getLocale);
        const urlParameter = { options };
        const requestUrl = buildUrl(urlTemplate(urlParameter), _merge(defaultFilters, options.filters), locale);
        const requestConfig = defaultOptions.requestConfig
          ? { ...defaultOptions.requestConfig, ...options.requestConfig }
          : options.requestConfig;

        logger.debug('Fetch entity data URL', url, 'defaultOptions', defaultOptions, 'Options', options);
        logger.debug('Fetch entity data RequestUrl', requestUrl, 'RequestConfig', requestConfig);
        const response = yield call(http.get, requestUrl, requestConfig);
        if (response.status < 200 || response.status > 299) {
          logger.error('Response with HTTP status', response.status, response);
          yield put(entityDescriptor.fetchDataFailure(response.data));
        } else {
          yield put(entityDescriptor.fetchDataComplete(responseProjection(response.data)));
        }
      }
    } catch (e) {
      logger.error('Exception occurred while fetching entity data', e);
      yield put(entityDescriptor.fetchDataFailure(e));
    }
  };
}

export function createInitWithDefaultData(entityDescriptor, options) {
  const { logger } = entityDescriptor;
  return function* initWithDefaultData({ payload: { initData } }) {
    if (!options.getDefaultData) {
      return;
    }
    const state = yield select();
    const data = yield select(entityDescriptor.selectors.getData);

    const defaultData = { ...options.getDefaultData(state), ...initData };
    logger.debug('Init with default data', defaultData, 'entityDescriptor', entityDescriptor);
    yield put(entityDescriptor.setData({ ...defaultData, ...(data || {}) }));
  };
}

export function createFetchDataActionTypes(actionPrefix) {
  return {
    FETCH_DATA: actionPrefix + '_FETCH_DATA',
    INIT_WITH_DEFAULT_DATA: actionPrefix + '_INIT_WITH_DEFAULT_DATA',
    SET_DATA: actionPrefix + '_SET_DATA',
    GET_OR_FETCH_DATA: actionPrefix + '_GET_OR_FETCH_DATA',
    FETCH_DATA_LOADING: actionPrefix + '_FETCH_DATA_LOADING',
    FETCH_DATA_COMPLETE: actionPrefix + '_FETCH_DATA_COMPLETE',
    FETCH_DATA_FAILURE: actionPrefix + '_FETCH_DATA_FAILURE',
    CLEAR_DATA: actionPrefix + '_CLEAR_DATA',
    DATA_REMOVE_ERROR: actionPrefix + '_DATA_REMOVE_ERROR',
    DATA_CLEAR_ERRORS: actionPrefix + '_DATA_CLEAR_ERRORS',
  };
}

export function createFetchDataActions(actionTypes) {
  return {
    getOrFetchData: options => ({ type: actionTypes.GET_OR_FETCH_DATA, payload: { options } }),
    setData: data => ({ type: actionTypes.SET_DATA, payload: { data } }),
    initWithDefaultData: initData => ({ type: actionTypes.INIT_WITH_DEFAULT_DATA, payload: { initData } }),
    fetchData: options => ({ type: actionTypes.FETCH_DATA, payload: { options } }),
    fetchDataLoading: loading => ({ type: actionTypes.FETCH_DATA_LOADING, payload: { loading } }),
    fetchDataComplete: data => ({
      type: actionTypes.FETCH_DATA_COMPLETE,
      payload: { data },
    }),
    fetchDataFailure: error => ({
      type: actionTypes.FETCH_DATA_FAILURE,
      payload: { error },
      error: true,
    }),
    clearData: context => ({ type: actionTypes.CLEAR_DATA, payload: { context } }),
    dataRemoveError: index => ({ type: actionTypes.DATA_REMOVE_ERROR, payload: { index } }),
    dataClearErrors: () => ({ type: actionTypes.DATA_CLEAR_ERRORS }),
  };
}

export function createFetchDataReduceFunctions(moduleDescriptor) {
  return {
    [moduleDescriptor.actionTypes.FETCH_DATA]: state => {
      return { ...state, dataLoading: true };
    },
    [moduleDescriptor.actionTypes.GET_OR_FETCH_DATA]: state => {
      return { ...state, dataLoading: true };
    },
    [moduleDescriptor.actionTypes.FETCH_DATA_COMPLETE]: (state, { data }) => ({ ...state, data, dataLoading: false }),
    [moduleDescriptor.actionTypes.SET_DATA]: (state, { data }) => ({ ...state, data, dataLoading: false }),
    [moduleDescriptor.actionTypes.FETCH_DATA_FAILURE]: (state, { error }) => ({
      ...state,
      dataErrors: [...state.dataErrors, error],
      dataLoading: false,
    }),
    [moduleDescriptor.actionTypes.CLEAR_DATA]: state => ({
      ...state,
      data: null,
      dataLoading: false,
      dataErrors: [],
    }),
    [moduleDescriptor.actionTypes.DATA_REMOVE_ERROR]: createErrorRemoveReducer('dataErrors'),
    [moduleDescriptor.actionTypes.DATA_CLEAR_ERRORS]: createErrorClearReducer('deleteErrors'),
  };
}

export function createFetchDataSelectors(moduleName, entity) {
  const selectEntity = state => state[moduleName][entity.name];

  return {
    isDataLoading: createSelector(selectEntity, entityState => entityState.dataLoading),
    getDefaultData: createSelector(selectEntity, entityState => entityState.defaultData),
    getDataErrors: createSelector(selectEntity, entityState => entityState.dataErrors),
    hasDataErrors: createSelector(selectEntity, entityState => entityState.dataErrors.length > 0),
    getData: createSelector(
      selectEntity,
      entity.__decorator ? entityState => decorateEntity(entityState.data, entity) : entityState => entityState.data
    ),
  };
}
